/**
 * Audio module for playing morse code tones
 * Handles Web Audio API initialization and tone generation
 */

import {
    calculateDotDurationMs,
    calculateDashDurationMs,
    calculateSymbolSpacingMs,
    calculateCharacterSpacingMs
} from './morse.js';

// Audio constants
const AUDIO_SAMPLE_RATE_HZ = 44100;
const AUDIO_ENVELOPE_ATTACK_SEC = 0.001;
const AUDIO_ENVELOPE_RELEASE_SEC = 0.002;

/**
 * Audio context manager - handles initialization and state
 */
export class AudioManager {
    constructor() {
        this.audioContext = null;
        this.masterGainNode = null;
        this.isInitialized = false;
        this.activeNodes = new Set();
        this.toneVolumeNormalized = 0.5; // Default 50% volume (0-1 range)
        // Incremented on every playback and on stopAll, so a stale
        // completion callback can detect it has been cancelled
        this.playbackToken = 0;
        this.completionTimer = null;
    }

    /**
     * Initialize the audio context and nodes
     * @returns {Promise<boolean>} Success status
     */
    async initialize() {
        if (this.isInitialized && this.audioContext?.state === 'running') {
            return true;
        }

        try {
            // Create audio context if it doesn't exist
            if (!this.audioContext) {
                const AudioContext = window.AudioContext || window.webkitAudioContext;
                if (!AudioContext) {
                    throw new Error('Web Audio API not supported');
                }

                // Create context with optimal settings
                const contextOptions = {
                    latencyHint: 'playback',
                    sampleRate: AUDIO_SAMPLE_RATE_HZ
                };

                try {
                    this.audioContext = new AudioContext(contextOptions);
                } catch (e) {
                    // Fallback for older browsers
                    this.audioContext = new AudioContext();
                }

                // Create master gain node
                this.masterGainNode = this.audioContext.createGain();
                this.masterGainNode.gain.value = 1.0;

                // Connect audio chain directly (no compression)
                this.masterGainNode.connect(this.audioContext.destination);
            }

            // Resume if suspended
            if (this.audioContext.state === 'suspended') {
                await this.audioContext.resume();
            }

            // Set audio session type to playback to allow audio in iOS silent mode
            if ('audioSession' in navigator) {
                navigator.audioSession.type = 'playback';
            }

            // Play silent buffer to unlock audio on iOS
            if (!this.isInitialized) {
                const silentBuffer = this.audioContext.createBuffer(1, 1, this.audioContext.sampleRate);
                const source = this.audioContext.createBufferSource();
                source.buffer = silentBuffer;
                source.connect(this.audioContext.destination);
                source.start(0);
            }

            this.isInitialized = true;
            return true;
        } catch (err) {
            console.error('Failed to initialize audio:', err);
            return false;
        }
    }

    /**
     * Set the tone volume
     * @param {number} volumePercent - Volume percentage (0-100)
     */
    setToneVolume(volumePercent) {
        // Clamp between 0 and 100, convert to 0-1 range
        this.toneVolumeNormalized = Math.max(0, Math.min(100, volumePercent)) / 100;
    }

    /**
     * Create and schedule a tone oscillator
     * @param {number} frequencyHz - Tone frequency in Hz
     * @param {number} startTimeSec - Start time in audio context time (seconds)
     * @param {number} durationSec - Duration in seconds
     * @returns {{oscillator: OscillatorNode, gainNode: GainNode}}
     */
    createToneOscillator(frequencyHz, startTimeSec, durationSec) {
        if (!this.audioContext || !this.isInitialized) {
            throw new Error('Audio not initialized');
        }

        // Create oscillator
        const oscillator = this.audioContext.createOscillator();
        oscillator.type = 'sine';
        oscillator.frequency.setValueAtTime(frequencyHz, startTimeSec);

        // Create gain node for envelope
        const gainNode = this.audioContext.createGain();

        // Apply envelope
        gainNode.gain.cancelScheduledValues(startTimeSec);
        gainNode.gain.setValueAtTime(0, startTimeSec);
        gainNode.gain.linearRampToValueAtTime(this.toneVolumeNormalized, startTimeSec + AUDIO_ENVELOPE_ATTACK_SEC);

        const holdTimeSec = Math.max(startTimeSec + AUDIO_ENVELOPE_ATTACK_SEC, startTimeSec + durationSec - AUDIO_ENVELOPE_RELEASE_SEC);
        gainNode.gain.setValueAtTime(this.toneVolumeNormalized, holdTimeSec);
        gainNode.gain.linearRampToValueAtTime(0, startTimeSec + durationSec);

        // Connect nodes
        oscillator.connect(gainNode);
        gainNode.connect(this.masterGainNode);

        // Schedule start and stop
        oscillator.start(startTimeSec);
        oscillator.stop(startTimeSec + durationSec + 0.001);

        // Track for cleanup
        this.activeNodes.add(oscillator);
        this.activeNodes.add(gainNode);

        // Cleanup on end
        oscillator.onended = () => {
            setTimeout(() => {
                this.activeNodes.delete(oscillator);
                this.activeNodes.delete(gainNode);
                try {
                    oscillator.disconnect();
                    gainNode.disconnect();
                } catch (e) {
                    // Already disconnected
                }
            }, 0);
        };

        return { oscillator, gainNode };
    }

    /**
     * Play a morse code sequence
     * @param {string} morseCode - Morse pattern (dots and dashes)
     * @param {number} wpm - Words per minute
     * @param {number} frequencyHz - Tone frequency in Hz
     * @param {Function} onComplete - Callback when playback completes
     */
    playMorseSequence(morseCode, wpm, frequencyHz, onComplete) {
        const token = ++this.playbackToken;

        // Completion is scheduled off the total playback duration, never
        // through oscillator.onended: stopping an oscillator fires onended,
        // which would make Stop trigger the completion chain.
        const scheduleCompletion = (delayMs) => {
            if (!onComplete) return;
            this.completionTimer = setTimeout(() => {
                if (token === this.playbackToken) {
                    onComplete();
                }
            }, delayMs);
        };

        // Without audio (not initialized, or empty pattern) still advance the chain
        if (!morseCode || !this.audioContext || !this.isInitialized) {
            scheduleCompletion(100);
            return;
        }

        const dotDurationMs = calculateDotDurationMs(wpm);
        const dashDurationMs = calculateDashDurationMs(wpm);
        const symbolSpacingMs = calculateSymbolSpacingMs(wpm);

        // Compensate for envelope
        const envelopeCompensationMs = (AUDIO_ENVELOPE_ATTACK_SEC + AUDIO_ENVELOPE_RELEASE_SEC) * 1000;
        const compensatedDotDurationSec = (dotDurationMs + envelopeCompensationMs) / 1000;
        const compensatedDashDurationSec = (dashDurationMs + envelopeCompensationMs) / 1000;
        const symbolSpacingSec = symbolSpacingMs / 1000;

        // Get current time for scheduling
        const startBaseSec = this.audioContext.currentTime;
        let startTimeSec = startBaseSec;

        // Schedule each symbol
        for (let i = 0; i < morseCode.length; i++) {
            const symbol = morseCode[i];

            if (symbol === '.') {
                this.createToneOscillator(frequencyHz, startTimeSec, compensatedDotDurationSec);
                startTimeSec += compensatedDotDurationSec;
            } else if (symbol === '-') {
                this.createToneOscillator(frequencyHz, startTimeSec, compensatedDashDurationSec);
                startTimeSec += compensatedDashDurationSec;
            }

            // Add inter-symbol spacing
            if (i < morseCode.length - 1) {
                startTimeSec += symbolSpacingSec;
            }
        }

        // Complete after the full pattern plus inter-character spacing
        const totalPlaybackMs = (startTimeSec - startBaseSec) * 1000;
        scheduleCompletion(totalPlaybackMs + calculateCharacterSpacingMs(wpm));
    }

    /**
     * Stop all currently playing sounds and cancel any pending completion
     */
    stopAll() {
        this.playbackToken++;
        if (this.completionTimer !== null) {
            clearTimeout(this.completionTimer);
            this.completionTimer = null;
        }
        this.activeNodes.forEach(node => {
            try {
                if (node.stop) {
                    node.stop();
                }
                if (node.disconnect) {
                    node.disconnect();
                }
            } catch (e) {
                // Ignore errors
            }
        });
        this.activeNodes.clear();
    }

    /**
     * Close the audio context
     */
    async close() {
        this.stopAll();
        if (this.audioContext && this.audioContext.state !== 'closed') {
            await this.audioContext.close();
        }
        this.isInitialized = false;
    }

    /**
     * Get current audio context state
     * @returns {string|null} Audio context state or null
     */
    getState() {
        return this.audioContext?.state || null;
    }
}

/**
 * Create a singleton instance
 */
export const audioManager = new AudioManager();

/**
 * Simple wrapper functions for backward compatibility
 */
export async function initAudio() {
    return audioManager.initialize();
}

export function stopSound() {
    audioManager.stopAll();
}

export function playMorseSequence(morseCode, wpm, frequencyHz, onComplete) {
    audioManager.playMorseSequence(morseCode, wpm, frequencyHz, onComplete);
}